import { BRAND_FONT_ROLES, BRAND_FONT_WEIGHTS } from '../constants/brandFonts';

const getFontFilesKey = (family) => `${family.regularUrl}|${family.boldUrl ?? ''}`;

/**
 * The roles of a K22 `brandFonts` payload that carry a font file, in `BRAND_FONT_ROLES` order. `aliasRole` is the
 * role whose alias the role renders with: a role with the same files as an earlier role (one family for heading and
 * body, PLAT-01's one-font rule) reuses that role's alias, so its files load once.
 * @param {{ heading: object|null, body: object|null }|null|undefined} brandFonts - The payload's `brandFonts`.
 * @returns {{ role: string, family: object, aliasRole: string }[]}
 */
export const getBrandFontRoles = (brandFonts) => {
  if (!brandFonts) {
    return [];
  }

  return Object.keys(BRAND_FONT_ROLES)
    .filter((role) => !!brandFonts[role]?.regularUrl)
    .reduce((roles, role) => {
      const family = brandFonts[role];
      const sameFiles = roles.find(({ family: other }) => getFontFilesKey(other) === getFontFilesKey(family));
      return [...roles, { role, family, aliasRole: sameFiles ? sameFiles.aliasRole : role }];
    }, []);
};

/**
 * Every font file of a `brandFonts` payload, each once, for `<link rel="preload">` on the server render: the files
 * then download while the page loads, and `FontFace.load()` reads them from the cache.
 * @param {{ heading: object|null, body: object|null }|null|undefined} brandFonts - The payload's `brandFonts`.
 * @returns {string[]}
 */
export const getBrandFontUrls = (brandFonts) => [
  ...new Set(
    getBrandFontRoles(brandFonts).flatMap(({ family }) => [family.regularUrl, family.boldUrl].filter(Boolean))
  ),
];

/**
 * A key that changes only when the font files change, so a gallery refresh with the same fonts loads nothing again.
 * @param {{ heading: object|null, body: object|null }|null|undefined} brandFonts - The payload's `brandFonts`.
 * @returns {string}
 */
export const getBrandFontsKey = (brandFonts) =>
  getBrandFontRoles(brandFonts)
    .map(({ role, family }) => `${role}:${family.regularUrl}:${family.boldUrl ?? ''}`)
    .join('|');

/**
 * The CSS variable value for a role: the alias it renders with, then the role's own stack (today's) for missing
 * glyphs.
 * @param {string} role - A `BRAND_FONT_ROLES` key.
 * @param {string} [aliasRole=role] - The role whose alias to use (`getBrandFontRoles`).
 * @returns {string}
 */
export const getBrandFontStack = (role, aliasRole = role) =>
  `"${BRAND_FONT_ROLES[aliasRole].alias}", ${BRAND_FONT_ROLES[role].fallbackStack}`;

/**
 * A CSS `url()` for a `FontFace` source. Quoted, so a URL with a space or a parenthesis (a catalog folder such as
 * `Open Sans/`) still parses; a quote or backslash in it is escaped.
 * @param {string} url - A font file URL.
 * @returns {string}
 */
export const toFontFaceSource = (url) => `url("${url.replace(/["\\]/g, '\\$&')}")`;

// Loads one file. It is not added to `document.fonts` here: the role decides that once it knows the regular file
// loaded.
const loadFontFace = (alias, url, weight) => new FontFace(alias, toFontFaceSource(url), { weight }).load();

/**
 * Loads one role's files under its alias, like `loadFonts` in `fonts.js` (print templates), which stays as it is.
 * The bold file is optional: when it fails, the browser makes the bold from the regular file. When the regular file
 * fails, nothing is added, not even a bold that loaded, so the alias never matches a lone bold file.
 * @param {string} role - A `BRAND_FONT_ROLES` key.
 * @param {{ regularUrl: string, boldUrl?: string }} family - The role's K21 `FontFamily`.
 * @returns {Promise<FontFace[]>} The faces added to `document.fonts`; empty when the role cannot be applied.
 */
export const loadBrandFontRole = async (role, family) => {
  const { alias } = BRAND_FONT_ROLES[role];

  const [regular, bold] = await Promise.all([
    loadFontFace(alias, family.regularUrl, BRAND_FONT_WEIGHTS.regular).catch(() => null),
    family.boldUrl ? loadFontFace(alias, family.boldUrl, BRAND_FONT_WEIGHTS.bold).catch(() => null) : null,
  ]);
  if (!regular) {
    return [];
  }

  const fontFaces = [regular, bold].filter(Boolean);
  fontFaces.forEach((fontFace) => document.fonts.add(fontFace));
  return fontFaces;
};

/**
 * Loads the brand fonts. Returns the CSS variables for the roles that loaded, and the faces it added, so the caller
 * can remove them when the fonts change: every role reuses one alias, so a face left behind would mix with the next
 * family's files. A role whose regular file fails is left out and keeps today's stack. Guests never see an error.
 * @param {{ heading: object|null, body: object|null }|null|undefined} brandFonts - The payload's `brandFonts`.
 * @returns {Promise<{ variables: Object<string, string>, fontFaces: FontFace[] }>}
 */
export const loadBrandFonts = async (brandFonts) => {
  const roles = getBrandFontRoles(brandFonts);
  if (!roles.length || typeof FontFace === 'undefined' || !document.fonts) {
    return { variables: {}, fontFaces: [] };
  }

  // Only a role that renders with its own alias loads files; a role that shares them uses its result.
  const ownRoles = roles.filter(({ role, aliasRole }) => role === aliasRole);
  const loaded = await Promise.all(ownRoles.map(({ role, family }) => loadBrandFontRole(role, family)));
  const fontFacesByRole = Object.fromEntries(ownRoles.map(({ role }, index) => [role, loaded[index]]));

  const variables = roles
    .filter(({ aliasRole }) => fontFacesByRole[aliasRole].length > 0)
    .reduce(
      (result, { role, aliasRole }) => ({
        ...result,
        [BRAND_FONT_ROLES[role].cssVariable]: getBrandFontStack(role, aliasRole),
      }),
      {}
    );

  return { variables, fontFaces: loaded.flat() };
};
